import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiCompass,
  FiMapPin,
  FiShield,
  FiStar,
  FiUsers,
} from "react-icons/fi";
import SafariList from "../components/SafariList";
import { safariTrips } from "../data/safariTrips";
import lions from "../assets/lions.jpg";
import beach from "../assets/beach.jpg";
import slava from "../assets/slava.jpg";

const revealUp = {
  hidden: { opacity: 0, y: 34 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const destinations = Array.from(
  new Set(
    safariTrips.flatMap((trip) => trip.countries || [trip.destination]).filter(Boolean)
  )
);

const stats = [
  { label: "Safari Packages", value: `${safariTrips.length}+`, icon: FiCompass },
  { label: "Countries", value: `${destinations.length}`, icon: FiMapPin },
  { label: "Private Trips", value: "Custom", icon: FiUsers },
  { label: "Guided Routes", value: "4x4", icon: FiShield },
];

const travelStyles = [
  "Gorilla Trekking",
  "Wildlife Safaris",
  "Desert Escapes",
  "Coastal Holidays",
  "Cultural Trips",
  "Private 4x4 Tours",
];

const Tours = () => {
  return (
    <div className="overflow-hidden bg-[#f6f1e7] font-[Poppins] text-[#082f1f]">
      <section className="relative min-h-[72vh] overflow-hidden pt-28 text-white">
        <img
          src={lions}
          alt="African safari lions"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#03140d]/95 via-[#03140d]/72 to-black/25" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#f6f1e7] to-transparent" />

        <div className="relative z-10 mx-auto flex min-h-[72vh] max-w-7xl items-center px-4 pb-20 sm:px-6 lg:px-8">
          <motion.div
            variants={revealUp}
            initial="hidden"
            animate="visible"
            className="max-w-4xl"
          >
            <div className="mb-5 inline-flex items-center gap-2 border-l-4 border-[#F5B731] bg-white/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-[#F5B731] backdrop-blur">
              <FiStar />
              Tour Packages
            </div>
            <h1 className="text-4xl font-extrabold uppercase leading-tight tracking-normal sm:text-5xl lg:text-6xl">
              Safari Trips & Travel Packages
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-white/80 sm:text-lg">
              Explore gorilla routes, classic wildlife safaris, Namibia desert
              escapes, cultural journeys, and coast extensions planned with
              smooth transfers, trusted guides, and flexible accommodation.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#safari-packages"
                className="inline-flex items-center gap-2 rounded-sm bg-[#F5B731] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-[#082f1f] transition hover:bg-white"
              >
                View Safaris
                <FiArrowRight />
              </a>
              <Link
                to="/contact"
                className="rounded-sm border border-white/30 px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition hover:border-[#F5B731] hover:text-[#F5B731]"
              >
                Request A Quote
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 -mt-12 pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-4 bg-white p-4 shadow-2xl shadow-black/10 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-4 border border-[#eadfbe] p-4"
              >
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-sm bg-[#082f1f] text-xl text-[#F5B731]">
                  <stat.icon />
                </span>
                <div>
                  <p className="text-2xl font-extrabold text-[#082f1f]">
                    {stat.value}
                  </p>
                  <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#5d7168]">
                    {stat.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
          <motion.div
            variants={revealUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.22 }}
          >
            <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
              Travel Styles
            </p>
            <h2 className="mt-3 text-3xl font-extrabold uppercase leading-tight text-[#082f1f] sm:text-4xl">
              Routes Built For Wildlife, Desert, Coast, And Culture
            </h2>
            <p className="mt-5 text-sm leading-7 text-[#5d7168]">
              Choose a ready-made safari or use one as a starting point for a
              private journey. Each route focuses on practical travel flow,
              strong experiences, and memorable places to stay.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {travelStyles.map((style) => (
                <span
                  key={style}
                  className="rounded-sm bg-[#f6f1e7] px-3 py-2 text-xs font-bold uppercase tracking-[0.08em] text-[#315245]"
                >
                  {style}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            variants={revealUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.22 }}
            className="grid gap-4 sm:grid-cols-2"
          >
            <div className="relative min-h-[320px] overflow-hidden">
              <img
                src={slava}
                alt="Giraffe on safari route"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F5B731]">
                  Wildlife
                </p>
                <h3 className="mt-2 text-2xl font-extrabold">Classic Safari</h3>
              </div>
            </div>
            <div className="relative min-h-[320px] overflow-hidden">
              <img
                src={beach}
                alt="Coastal travel escape"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#F5B731]">
                  Coast
                </p>
                <h3 className="mt-2 text-2xl font-extrabold">Beach Extension</h3>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="safari-packages" className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.22em] text-[#C88804]">
                Safari Trips
              </p>
              <h2 className="mt-3 max-w-3xl text-3xl font-extrabold uppercase leading-tight text-[#082f1f] sm:text-4xl">
                Choose Your Next Tour & Travel Experience
              </h2>
            </div>
            <p className="max-w-xl text-sm leading-7 text-[#5d7168]">
              Namibia is now included alongside Uganda, Kenya, Tanzania,
              Zanzibar, and regional safari combinations.
            </p>
          </div>

          <SafariList />
        </div>
      </section>
    </div>
  );
};

export default Tours;
